import { OfferHighlight } from "components/OfferHighlights/OfferHighlights";
import {
  ExclusiveOfferFeatureFragment,
  LocationFilterFragment,
  Maybe,
  OfferPerAirPromoQuery,
  OfferPerPromoQuery,
  SanityMultilingualBlockContent,
  SanityMultilingualString
} from "types/graphql-types";
import { OfferFromApi } from "types/api/exclusive_offers.d";
import { matchesAny } from "../../utils/localization/geolocation";
import FYCQueryBuilder from "components/FindYourCruise/FYCQueryBuilder";
import { LocalizedOffer } from "./Offer";
import { isPresent } from "src/utils/checks";

export const defaultLocalizedOffer: LocalizedOffer = {
  title: undefined,
  highlightsTitle: undefined,
  offerHighlights: [],
  termsAndConditionsFootnote: undefined,
  data: undefined
};

export const conditionFiltersMatch = (
  conditionFilters?: (string | undefined)[],
  condition?: string
): boolean =>
  !condition ||
  !conditionFilters ||
  !conditionFilters.length ||
  conditionFilters.includes(condition) ||
  conditionFilters.includes("*");

export type FilterableContent = {
  conditionFilters?: Maybe<Array<Maybe<string>>>;
  locationFilters?: (LocationFilterFragment | undefined)[];
};

export const matchesLocationAndCondition = <T extends FilterableContent>(
  condition?: string,
  countryCode?: string
) => (content: T | undefined): boolean =>
  conditionFiltersMatch(content?.conditionFilters, condition) &&
  matchesAny(content?.locationFilters, countryCode);

export const filterContent = <T>(
  condition: string | undefined,
  countryCode: string | undefined,
  content:
    | ((FilterableContent & { content?: Maybe<T> }) | undefined)[]
    | undefined
): T | undefined =>
  (content || []).find(matchesLocationAndCondition(condition, countryCode))
    ?.content;

export const toOfferHighlight = (
  highlight: ExclusiveOfferFeatureFragment
): OfferHighlight => ({
  name: highlight.name?.localized,
  description: highlight.description?.localized,
  icon: highlight.icon
});

export const filterHighlights = (
  condition?: string,
  countryCode?: string,
  offerHighlights?: ExclusiveOfferFeatureFragment[]
): OfferHighlight[] =>
  (offerHighlights || [])
    .filter(matchesLocationAndCondition(condition, countryCode))
    .map(toOfferHighlight);

export function getCruisePerimeterAlgoliaQuery(
  queryBuilder: FYCQueryBuilder,
  offerData: OfferFromApi | undefined
): FYCQueryBuilder {
  if (!offerData) return queryBuilder;

  const cruisePerimeter = offerData.cruisePerimeter;
  if (cruisePerimeter.type == "SomeCruises") {
    queryBuilder.filterByCruiseCodes(cruisePerimeter.cruiseCodes);
  }

  return queryBuilder;
}

export const offerPerPromoToLocalizedOffer = (
  offer: OfferPerPromoQuery["offer"],
  countryCode?: string
) => (offerData: OfferFromApi): LocalizedOffer => {
  const offerHighlights = filterHighlights(
    offerData?.condition,
    countryCode,
    offer?.features?.filter(isPresent)
  );
  const termsAndConditionsFootnote = filterContent(
    offerData?.condition,
    countryCode,
    offer?.termsAndConditionsFootnote
  );

  return {
    title: offer?.title,
    highlightsTitle: offer?.featureTitle,
    offerHighlights,
    termsAndConditionsFootnote,
    data: offerData
  };
};

export const offerPerAirPromoToLocalizedOffer = (
  offer: OfferPerAirPromoQuery["offer"],
  countryCode?: string
) => (offerData: OfferFromApi): LocalizedOffer => {
  const title = filterContent<SanityMultilingualBlockContent>(
    offerData?.condition,
    countryCode,
    offer?.title
  );
  const highlightsTitle = filterContent<SanityMultilingualString>(
    offerData?.condition,
    countryCode,
    offer?.featureTitle
  );
  const offerHighlights = filterHighlights(
    offerData?.condition,
    countryCode,
    offer?.features?.filter(isPresent)
  );
  const termsAndConditionsFootnote = filterContent(
    offerData?.condition,
    countryCode,
    offer?.termsAndConditionsFootnote
  );

  return {
    title,
    highlightsTitle,
    offerHighlights,
    termsAndConditionsFootnote,
    data: offerData
  };
};
